/**
 * Touch gestures on the view (phones and tablets), without the DOM so that they can be tested:
 * - one finger: either it aims (`down` answers 'aim': its moves are reported as aim, its release as `aimEnd`), or it
 *   moves the view once it has moved more than TAP_SLOP pixels, and a short touch that barely moved is a `tap`;
 * - two fingers: they move the view (the middle between them) and pinch (`pinch`, the ratio of their distances since
 *   the two touched); an aim under way is cancelled. Once two fingers touched, nothing more happens until all are up.
 */

/** Pixels a finger may move and still be a tap. */
export const TAP_SLOP = 10;
/** A touch longer than this is not a tap (it was a press that went nowhere). */
export const TAP_MS = 500;

export interface GestureHandlers {
  /** A first finger touched at (x, y): 'aim' to aim with it, 'pan' to move the view (and tap). */
  down(x: number, y: number): 'aim' | 'pan';
  /** The aiming finger is at (x, y) (also right after `down`). */
  aim(x: number, y: number): void;
  /** The aiming finger was lifted at (x, y); `cancelled`: a second finger took over. */
  aimEnd(x: number, y: number, cancelled: boolean): void;
  tap(x: number, y: number): void;
  /** Move the view by (dx, dy) screen pixels. */
  pan(dx: number, dy: number): void;
  /** Two fingers: their distance is `ratio` times the one they had when they touched, around (cx, cy). */
  pinch(ratio: number, cx: number, cy: number): void;
}

interface Finger { x: number; y: number }

export class Gestures {
  private readonly fingers = new Map<number, Finger>();
  private mode: 'none' | 'aim' | 'pan' | 'two' | 'done' = 'none';
  private start = { x: 0, y: 0, at: 0, moved: false };
  private pinchFrom = 0;
  private middle = { x: 0, y: 0 };

  constructor(private readonly h: GestureHandlers) {}

  get active(): boolean { return this.fingers.size > 0; }

  down(id: number, x: number, y: number, now: number): void {
    this.fingers.set(id, { x, y });
    if (this.fingers.size === 1 && this.mode === 'none') {
      this.start = { x, y, at: now, moved: false };
      this.mode = this.h.down(x, y);
      if (this.mode === 'aim') this.h.aim(x, y);
      return;
    }
    if (this.fingers.size === 2 && (this.mode === 'aim' || this.mode === 'pan')) {
      if (this.mode === 'aim') { const f = this.first(); this.h.aimEnd(f.x, f.y, true); }
      this.mode = 'two';
      this.pinchFrom = this.spread();
      this.middle = this.centre();
    }
  }

  move(id: number, x: number, y: number): void {
    const f = this.fingers.get(id);
    if (!f) return;
    const dx = x - f.x;
    const dy = y - f.y;
    f.x = x;
    f.y = y;
    if (this.mode === 'aim') this.h.aim(x, y);
    else if (this.mode === 'pan') {
      if (!this.start.moved && Math.hypot(x - this.start.x, y - this.start.y) > TAP_SLOP) {
        this.start.moved = true;
        this.h.pan(x - this.start.x, y - this.start.y); // the slop too: the view stays under the finger
      } else if (this.start.moved) this.h.pan(dx, dy);
    } else if (this.mode === 'two' && this.fingers.size >= 2) {
      const c = this.centre();
      this.h.pan(c.x - this.middle.x, c.y - this.middle.y);
      this.middle = c;
      if (this.pinchFrom > 0) this.h.pinch(this.spread() / this.pinchFrom, c.x, c.y);
    }
  }

  up(id: number, x: number, y: number, now: number): void {
    if (!this.fingers.has(id)) return;
    this.move(id, x, y);
    this.fingers.delete(id);
    if (this.mode === 'aim') this.h.aimEnd(x, y, false);
    else if (this.mode === 'pan' && !this.start.moved && now - this.start.at <= TAP_MS) this.h.tap(this.start.x, this.start.y);
    this.end();
  }

  /** The system took the touch (a call, a gesture of the browser): nothing fires. */
  cancel(id: number): void {
    if (!this.fingers.has(id)) return;
    const f = this.fingers.get(id)!;
    this.fingers.delete(id);
    if (this.mode === 'aim') this.h.aimEnd(f.x, f.y, true);
    this.end();
  }

  /** The pinch starts again from the fingers' distance now (after the view changed its zoom). */
  rebasePinch(): void { this.pinchFrom = this.spread(); }

  private end(): void {
    if (this.fingers.size === 0) this.mode = 'none';
    else if (this.mode !== 'none') this.mode = 'done';
  }

  private first(): Finger { return this.fingers.values().next().value as Finger; }

  private centre(): Finger {
    const [a, b] = [...this.fingers.values()];
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }

  private spread(): number {
    const [a, b] = [...this.fingers.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }
}

/** The walking pad: a thumb nearer its middle than this share of its radius does not walk. */
export const PAD_DEAD_ZONE = 0.3;

/**
 * The walking pad (a circle the thumb slides in): (dx, dy) is the thumb from the middle of the pad, in pixels.
 * Returns where the knob is drawn (held inside the circle) and the walking direction: -1 left, 1 right, 0 none.
 * Only the side counts (the pad walks; JUMP jumps): a thumb past the dead zone walks as soon as it leans sideways,
 * unless it points almost straight up or down (within 22.5°), so that a thumb that slips does not walk.
 */
export function padState(dx: number, dy: number, radius: number): { kx: number; ky: number; dir: -1 | 0 | 1 } {
  const d = Math.hypot(dx, dy);
  const k = d > radius ? radius / d : 1;
  const kx = dx * k;
  const ky = dy * k;
  // tan(22.5°) ≈ 0.414: |dx| / |dy| below it, the thumb points up or down.
  const dir = d < PAD_DEAD_ZONE * radius || Math.abs(dx) < 0.414 * Math.abs(dy) ? 0 : dx < 0 ? -1 : 1;
  return { kx, ky, dir };
}
